import { useMyAccountHasFeature } from '@/api/queries/me/useGetMyAccountFeatures';
import { useSelector } from '@/api/state';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { useProjectCustomFields } from '@/hooks/useProjectCustomFields';
import CreateEditTaskSidebarControl from '@/ui/components/createEditTaskSidebar/CreateEditTaskSidebarControl';
import { CreateEditTaskFormValues } from '@/ui/components/createEditTaskSidebar/types';
import { Alert, Sidebar } from '@payaca/ambrosia';
import Modal from '@payaca/components/modal/Modal';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { TasksPermissions } from '@payaca/permissions/tasks/tasks.permissions';
import * as formActions from '@payaca/store/forms/formsActions';
import {
  PublicEntityTemplate,
  TemplatableEntity,
} from '@payaca/types/entity-templates';
import { WithOptional } from '@payaca/utilities/types';
import { FC, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import CreateEditProposalTemplateControl from '../createEditProposalTemplateControl/CreateEditProposalTemplateControl';
import CreateEditScheduledEventTemplateControl from '../createEditScheduledEventTemplateControl/CreateEditScheduledEventTemplateControl';

const buildTaskSidebarDefaultValues = (
  template?: WithOptional<PublicEntityTemplate<'task'>, 'publicId'>
): CreateEditTaskFormValues => {
  if (template?.entityType === 'task') {
    const base = {
      templateName: template.name || undefined,
      templateDescription: template.description || undefined,
      notes: template.data.notes,
    };

    switch (true) {
      case !!template.data.customFieldIdentifiers &&
        template.data.customFieldIdentifiers.length > 0:
        return {
          ...base,
          type: 'custom-fields' as const,
          customFieldIdentifier: template.data.customFieldIdentifiers[0],
        };
      case !!template.data.formTemplateId:
        return {
          ...base,
          type: 'form',
          formId: Number(template.data.formTemplateId),
        };
      default:
        return {
          ...base,
          type: 'checklist',
          name: template.data.name || '',
          checklistItems: (template.data.checklistItems || []).map((i) => ({
            label: i,
          })),
        };
    }
  }

  return {
    type: 'checklist',
    name: '',
    checklistItems: [],
  };
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  template?: WithOptional<PublicEntityTemplate<TemplatableEntity>, 'publicId'>;
  allowModifyTemplateNameDescription?: boolean;
  isChildOfScheduledEvent?: boolean;
  onRequestSave: (
    entityTemplate: Omit<PublicEntityTemplate<TemplatableEntity>, 'publicId'>
  ) => void;
  action?: 'Create' | 'Edit';
  isProcessing?: boolean;
  errorMessage?: string;
};
const CreateEditEntityTemplateModal: FC<Props> = ({
  isOpen,
  onClose,
  template,
  allowModifyTemplateNameDescription,
  isChildOfScheduledEvent = false,
  onRequestSave,
  action,
  isProcessing = false,
  errorMessage,
}: Props): JSX.Element | null => {
  const hasPermissionToPersistCustomFieldTask = useUserHasPermission({
    permissions: [TasksPermissions.PERSIST_CUSTOM_FIELD_TASK],
  });

  const hasPermissionToManageCustomFieldValues = useUserHasPermission({
    permissions: [AccountsPermissions.MANAGE_CUSTOM_FIELD_VALUES],
  });

  const canPersistFieldsetTask =
    hasPermissionToPersistCustomFieldTask &&
    hasPermissionToManageCustomFieldValues;

  const hasLegacyFormTemplates = useMyAccountHasFeature(
    'hasLegacyFormTemplates'
  );

  const title = useMemo(() => {
    return `${action || (template?.publicId ? 'Edit' : 'Create')} ${
      template?.entityType == 'task'
        ? 'Task'
        : template?.entityType == 'scheduledEvent'
          ? 'Event'
          : 'Proposal'
    } Template`;
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'action'
  }, [template]);

  const { projectCustomFields } = useProjectCustomFields(['fieldset']);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(formActions.requestGetAvailableForms());
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- dispatch is stable
  }, []);

  const availableForms = useSelector((state) => state.forms.availableForms);

  const isPdfFormTemplate = useMemo(() => {
    if (template?.entityType !== 'task') return false;
    const t = template as PublicEntityTemplate<'task'>;
    if (!t.data.formTemplateId) return false;
    const form = availableForms.find(
      (f) => f.value.toString() === t.data.formTemplateId
    );
    return form?.type === 'pdf';
  }, [template, availableForms]);

  if (template?.entityType === 'task') {
    return (
      <Sidebar isOpen={isOpen} onClose={onClose} title={title}>
        {errorMessage && (
          <Alert colour="red" className="m-4">
            {errorMessage}
          </Alert>
        )}
        {isPdfFormTemplate ? (
          <Alert colour="yellow" className="m-4">
            This Task Template uses a PDF Form, which is no longer supported.
            Please delete this Task Template and create a new one using an
            alternative Task type.
          </Alert>
        ) : (
          <CreateEditTaskSidebarControl
            isEditing={action === 'Edit'}
            formType={
              isChildOfScheduledEvent
                ? 'task-template'
                : 'task-template-with-identifier'
            }
            onSubmit={(newTaskTemplate) => {
              let entityTemplateData: PublicEntityTemplate<'task'>['data'] = {
                notes: newTaskTemplate.notes,
              };

              switch (newTaskTemplate.type) {
                case 'checklist':
                  entityTemplateData = {
                    ...entityTemplateData,
                    name: newTaskTemplate.name,
                    checklistItems: newTaskTemplate.checklistItems.map(
                      (item) => item.label
                    ),
                  };
                  break;
                case 'form': {
                  const selectedForm = availableForms.find(
                    (form) => form.value === newTaskTemplate.formId
                  );

                  entityTemplateData = {
                    ...entityTemplateData,
                    name: selectedForm?.label,
                    formTemplateId: newTaskTemplate.formId?.toString(),
                  };
                  break;
                }
                case 'custom-fields': {
                  const selectedCustomField = projectCustomFields.find(
                    (i) =>
                      i.identifier === newTaskTemplate.customFieldIdentifier
                  );

                  entityTemplateData = {
                    ...entityTemplateData,
                    name: selectedCustomField?.label,
                    customFieldIdentifiers:
                      newTaskTemplate.customFieldIdentifier
                        ? [newTaskTemplate.customFieldIdentifier]
                        : undefined,
                  };
                  break;
                }
              }

              onRequestSave({
                entityType: 'task' as TemplatableEntity,
                name: newTaskTemplate.templateName,
                description: newTaskTemplate.templateDescription,
                data: entityTemplateData,
              });
            }}
            isProcessing={isProcessing}
            defaultValues={buildTaskSidebarDefaultValues(
              template as WithOptional<PublicEntityTemplate<'task'>, 'publicId'>
            )}
            setIsDirty={() => {}}
            canCreateFormTask={hasLegacyFormTemplates && !isPdfFormTemplate}
            canCreateCustomFieldTask={
              isChildOfScheduledEvent && canPersistFieldsetTask
            }
          />
        )}
      </Sidebar>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {errorMessage && (
        <Alert colour="red" className="mb-4">
          {errorMessage}
        </Alert>
      )}
      {template?.entityType === 'scheduledEvent' && (
        <CreateEditScheduledEventTemplateControl
          allowModifyTemplateNameDescription={
            allowModifyTemplateNameDescription
          }
          scheduledEventTemplate={
            template as Omit<PublicEntityTemplate<'scheduledEvent'>, 'publicId'>
          }
          onRequestSave={onRequestSave}
          isProcessing={isProcessing}
        />
      )}
      {template?.entityType === 'proposal' && (
        <CreateEditProposalTemplateControl
          allowModifyTemplateNameDescription={
            allowModifyTemplateNameDescription
          }
          proposalTemplate={
            template as Omit<PublicEntityTemplate<'proposal'>, 'publicId'>
          }
          onRequestSave={onRequestSave}
        />
      )}
    </Modal>
  );
};
export default CreateEditEntityTemplateModal;
